import React from 'react';
import QUERIES, { Main, Midi, Full } from '../theming/styles';
import styled, { createGlobalStyle } from 'styled-components';
import { Nav } from '../components';
import { SiteFooter } from '../components/foot/SiteFooter';
import { Spread } from '../components/switch/styles';
import OutlineScheduler from '../components/OutlineScheduler';

const ImagePlaceholder = styled.div`
    width: 100%;
    background: rgba(255,255,255,0.03);
    border: 2px dashed rgba(255,255,255,0.10);
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 24px;
    gap: 10px;
    min-height: ${({ $height }) => $height || '320px'};
    margin: ${({ $margin }) => $margin || '32px 0'};
`;

const PlaceholderLabel = styled.p`
    font-family: var(--font-body);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.25;
    margin: 0;
`;

const PlaceholderTitle = styled.p`
    font-family: var(--font-body);
    font-size: 13px;
    color: ${({ theme }) => theme.textMain};
    opacity: 0.4;
    text-align: center;
    margin: 0;
`;

const FlexGroup = styled.div`
    margin: 40px 0;
    display: flex;
    justify-content: space-between;
    gap: 30px;
    flex-wrap: wrap;
    background-color: rgb(255 255 255 / 40%);
    padding: 30px 30px;
    border-radius: 10px;

    div {
        width: 100%;
    
        @media (${QUERIES.medium}) {
            width: 45%;
        }
    }

    @media (${QUERIES.large}) {
        flex-wrap: nowrap;
        gap: 50px;
        padding: 30px 50px;
        margin: 60px 0;
    }
`;


const GlobalStyle = createGlobalStyle`
  html {
    scroll-behavior: smooth;
  }
`;

const CaseHeader = styled.div``;

const PageLayout = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    align-items: flex-start;
    padding: 0 20px;
`;

const NavColumn = styled.div`
    width: 18%;
    flex-shrink: 0;
    position: sticky;
    top: 90px;
    align-self: flex-start;
    padding-top: 52px;

    @media (max-width: 1099px) {
        display: none;
    }
`;

const ContentColumn = styled.div`
    flex: 1;
    min-width: 0;
`;

const Scheduler = ({ toggleMode, mode, spread, setDisableScroll }) => {
    const spreadClass =
        spread === 'first'
            ? 'growBlack'
            : spread === 'second'
            ? 'growWhite'
            : '';

    return (
        <Main style={{
            position: 'relative',
            overflow: setDisableScroll ? 'hidden' : 'null',
            height: setDisableScroll ? '100vh' : 'unset',
        }}>
            <GlobalStyle />
            <Spread className={spreadClass} />
            <Nav mode={mode} toggleMode={toggleMode} />
            <PageLayout>
                <NavColumn>
                    <OutlineScheduler />
                </NavColumn>
                <ContentColumn>
            <CaseHeader id="case-study-header" className="card-pad">
                <h5>Case Study</h5>

                <h1 id="my-safaricom-partner-portal-redesign" style={{ marginBottom: 35, maxWidth: 'none' }}>
                    Empowering the Backbone of Trade: The My Safaricom Partner Portal Redesign
                </h1>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", background: "var(--color-surface)", borderRadius: 18, padding: 8, margin: "32px 0" }}>
                    {[
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
                            label: "Timeline",
                            body: <>2023, <span style={{ opacity: 0.5 }}>Discovery & Research</span><br />2023, <span style={{ opacity: 0.5 }}>Design & Build</span><br />2023, <span style={{ opacity: 0.5 }}>MVP Launch</span></>
                        },
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z"/><path d="M16 3h-8a2 2 0 0 0-2 2v2h12V5a2 2 0 0 0-2-2z"/><line x1="12" y1="12" x2="12" y2="12"/></svg>,
                            label: "My Role",
                            body: <>Lead UI/UX Designer<br />End-to-End Design</>
                        },
                    ].map(({ icon, label, body }) => (
                        <div key={label} style={{ flex: 1, minWidth: 180, background: "var(--color-bg)", borderRadius: 12, padding: "20px" }}>
                            <div style={{ color: "var(--color-text)", marginBottom: 20, opacity: 0.6 }}>{icon}</div>
                            <h5 style={{ marginBottom: 10 }}>{label}</h5>
                            <p className="text-style">{body}</p>
                        </div>
                    ))}
                </div>
                <p className="text-style">
                    In the high-stakes world of Kenyan telecommunications, dealers are the lifeblood of the ecosystem. Yet, until 2023, these essential partners were forced to navigate a fragmented, legacy landscape of multiple logins, blind ordering, and manual reporting. As the Lead UI/UX Designer, I spearheaded the end-to-end redesign of the My Safaricom Partner Portal — unifying disparate services into a single, scalable SaaS platform.
                </p>
            </CaseHeader>

            <Full>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <ImagePlaceholder $height="480px" $margin="0 0 0 0">
                        <PlaceholderLabel>Image Placeholder</PlaceholderLabel>
                        <PlaceholderTitle>Final shipped design of the My Safaricom Partner Portal</PlaceholderTitle>
                    </ImagePlaceholder>
                    <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>Final shipped design of the My Safaricom Partner Portal</p>
                </div>
            </Full>
            {/* ── IMPACT NUMBERS ── */}
            <Midi style={{ marginBottom: 0 }}>
                <h3 id="results-at-a-glance" style={{ margin: "0 0 24px" }}>Results at a Glance</h3>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", background: "var(--color-surface)", borderRadius: 18, padding: 8 }}>
                    {[
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
                            label: "Dealers Onboarded",
                            body: "4,700+ with 100% conversion on core journeys"
                        },
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M12 12h.01"/><path d="M17 12h.01"/><path d="M7 12h.01"/></svg>,
                            label: "Order Revenue",
                            body: "90M KES generated within the first month of launch"
                        },
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>,
                            label: "NPS Score",
                            body: "75.5 — world-class for a mandatory B2B enterprise tool"
                        },
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 1.27h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.86a16 16 0 0 0 6.13 6.13l.86-.86a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
                            label: "Support Deflection",
                            body: "91% of issues resolved directly, reducing L300 escalations"
                        },
                    ].map(({ icon, label, body }) => (
                        <div key={label} style={{ flex: 1, minWidth: 180, background: "var(--color-bg)", borderRadius: 12, padding: "20px" }}>
                            <div style={{ color: "var(--color-text)", marginBottom: 20, opacity: 0.6 }}>{icon}</div>
                            <h5 style={{ marginBottom: 10 }}>{label}</h5>
                            <p className="text-style">{body}</p>
                        </div>
                    ))}
                </div>
            </Midi>

            {/* ── THE HOOK ── */}
            <Midi style={{ marginBottom: 0 }}>
                <h3 id="the-challenge" style={{ margin: "0 0 24px" }}>
                    A Day in the Life of Frustration
                </h3>
                <blockquote style={{ borderLeft: "3px solid var(--color-primary)", padding: "10px 20px", margin: "24px 0", fontStyle: "italic", color: "var(--color-primary)", fontFamily: "var(--font-display)", fontSize: "18px", letterSpacing: "-0.3px" }}>
                    "Order Cancelled: Out of Stock." — Three days after placing a 10M KES order.
                </blockquote>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    Imagine you are Ali, a dealer managing multiple M-PESA agents and dealerships across Nairobi. Your morning begins not with strategy, but with a frantic search for passwords. You have <mark>five different sites for five different services</mark> — KYC, sales support, inventory, financing, and reporting.
                </p>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    Yesterday, you placed a high-stakes order worth 10M KES. You wait for the stock to arrive, only to receive that notification three days later. The system didn't show you the inventory levels. Now, your capital is tied up, your customers are walking away, and your refund is stuck in a manual "frustration loop."
                </p>
                <p className="text-style">
                    This wasn't just a "bad user experience." It was a <mark>systemic bottleneck costing Safaricom revenue</mark> and eroding the trust of its most valuable partners.
                </p>
                <Full>
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                        <ImagePlaceholder $height="280px">
                            <PlaceholderLabel>Image Placeholder</PlaceholderLabel>
                            <PlaceholderTitle>The fragmented legacy portal experience — 5 separate logins</PlaceholderTitle>
                        </ImagePlaceholder>
                        <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>Screenshot — The fragmented legacy portal experience (5 separate logins)</p>
                    </div>
                </Full>
            </Midi>
            {/* ── VISION ── */}
            <Midi style={{ marginBottom: 0 }}>
                <h3 id="the-vision" style={{ margin: "0 0 24px" }}>
                    The Vision: One Portal to Rule Them All
                </h3>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    The mission was clear but daunting: <mark>Unify, Standardize, and Scale.</mark> We needed to move away from the "Safaricom Dealer Portal" — a collection of silos — to the "My Safaricom Partner Portal" — a unified ecosystem.
                </p>
                <p className="text-style" style={{ marginBottom: 16 }}>
                    Our strategic North Star was built on five pillars: <mark>Centralization</mark> (SSO for all services), <mark>Transparency</mark> (real-time stock and reporting), <mark>Security</mark> (hardened OTP authentication), <mark>Autonomy</mark> (DIY service requests), and <mark>Scalability</mark> (Atomic Design system with standardized tokens).
                </p>
            </Midi>

            {/* ── DISCOVERY ── */}
            <Midi style={{ marginBottom: 0 }}>
                <h3 id="discovery-phase" style={{ margin: "0 0 24px" }}>
                    Discovery: Uncovering the "Why"
                </h3>
                <h3 id="four-friction-points" style={{ margin: "0 0 16px" }}>
                    Four Critical Friction Points
                </h3>
                <p className="text-style">
                    We didn't start with wireframes; we started with people. Through guerrilla research and in-depth interviews, we identified <mark>four critical friction points:</mark>
                </p>
                <ul style={{ listStyle: "none", margin: "20px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                        { label: "The Password Tax", body: "Dealers stored sensitive credentials on paper notes just to keep up with multiple platforms." },
                        { label: 'The "Black Box" of Reporting', body: "Operational data was fragmented and delayed. Dealers were flying blind, making guesses rather than data-driven decisions." },
                        { label: "The Ghost Inventory", body: 'The ordering journey lacked real-time stock validation, leading to the dreaded "cancelled order" cycle.' },
                        { label: "The Security Gap", body: "Legacy systems allowed internal staff to manipulate nominated numbers, creating a massive trust deficit around transaction authorization." },
                    ].map(({ label, body }) => (
                        <li key={label} style={{ display: "flex", gap: 16, alignItems: "flex-start", background: "var(--color-surface)", borderRadius: 10, padding: "16px 20px" }}>
                            <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", background: "var(--color-primary)", marginTop: 7 }} />
                            <span className="text-style" style={{ margin: 0 }}>
                                <strong style={{ color: "var(--color-primary)", display: "block", marginBottom: 4 }}>{label}</strong>
                                {body}
                            </span>
                        </li>
                    ))}
                </ul>

                <h3 id="the-personas" style={{ margin: "0 0 16px" }}>
                    The Personas
                </h3>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    We grounded our design in two primary archetypes:
                </p>
            </Midi>

            <Full>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <ImagePlaceholder $height="260px">
                        <PlaceholderLabel>Image Placeholder</PlaceholderLabel>
                        <PlaceholderTitle>Research — Affinity diagram / interview notes</PlaceholderTitle>
                    </ImagePlaceholder>
                    <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>Research — Affinity diagram / interview notes</p>
                </div>
            </Full>

            <Midi style={{ marginBottom: 0 }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", background: "var(--color-surface)", borderRadius: 18, padding: 8, margin: "20px 0 0" }}>
                    {[
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
                            label: "Ali — The Dealer",
                            body: "Needs speed, visibility, and reliability to scale his business across multiple M-PESA agents and dealerships."
                        },
                        {
                            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>,
                            label: "Sunnex — Dealer Support",
                            body: "Needs fewer escalations and a standardized onboarding process to meet his SLAs."
                        },
                    ].map(({ icon, label, body }) => (
                        <div key={label} style={{ flex: 1, minWidth: 220, background: "var(--color-bg)", borderRadius: 12, padding: "20px" }}>
                            <div style={{ color: "var(--color-text)", marginBottom: 20, opacity: 0.6 }}>{icon}</div>
                            <h5 style={{ marginBottom: 10 }}>{label}</h5>
                            <p className="text-style">{body}</p>
                        </div>
                    ))}
                </div>
            </Midi>

            {/* ── SOLUTION ── */}
            <Midi style={{ marginBottom: 0 }}>
                <h3 id="the-solution" style={{ margin: "0 0 24px" }}>
                    The Solution: Designing for High-Velocity Commerce
                </h3>

                <h3 id="frictionless-ordering" style={{ margin: "0 0 16px" }}>
                    1. Frictionless Ordering
                </h3>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    The ordering journey is where the business lives. By mapping mini-stores directly to the dealer's view, we ensured they could only order what was actually on the shelf — <mark>eliminating the cancelled order cycle entirely.</mark>
                </p>
                <ul style={{ listStyle: "none", margin: "20px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                        { label: "Real-Time Stock Visibility", body: "Dealers see live inventory before placing orders." },
                        { label: "Cart Feature", body: "Multi-product orders in a single session — a leap from the one-at-a-time legacy flow." },
                        { label: "ERP Balance at Checkout", body: "Surfacing the credit limit directly in checkout gave dealers the confidence that their order would go through." },
                    ].map(({ label, body }) => (
                        <li key={label} style={{ display: "flex", gap: 16, alignItems: "flex-start", background: "var(--color-surface)", borderRadius: 10, padding: "16px 20px" }}>
                            <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", background: "var(--color-primary)", marginTop: 7 }} />
                            <span className="text-style" style={{ margin: 0 }}>
                                <strong style={{ color: "var(--color-primary)", display: "block", marginBottom: 4 }}>{label}</strong>
                                {body}
                            </span>
                        </li>
                    ))}
                </ul>
            </Midi>
            <Full>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <ImagePlaceholder $height="400px">
                        <PlaceholderLabel>Image Placeholder</PlaceholderLabel>
                        <PlaceholderTitle>Ordering flow — Real-time stock visibility + cart screens</PlaceholderTitle>
                    </ImagePlaceholder>
                    <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>Ordering flow — Real-time stock visibility + cart screens</p>
                </div>
            </Full>

            <Midi style={{ marginBottom: 0 }}>
                <h3 id="automated-reporting" style={{ margin: "0 0 16px" }}>
                    2. Automated Reporting & Dashboard
                </h3>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    Previously, reports were Excel sheets living on various hard drives. We transformed this into an intuitive, visual dashboard — moving from <mark>"What happened last week?"</mark> to <mark>"How am I performing right now?"</mark>
                </p>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    This created a Single Source of Truth, fostering a culture of transparency that immediately boosted partner trust.
                </p>
            </Midi>
            <Full>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <ImagePlaceholder $height="380px">
                        <PlaceholderLabel>Image Placeholder</PlaceholderLabel>
                        <PlaceholderTitle>Dashboard — Automated reporting & performance metrics</PlaceholderTitle>
                    </ImagePlaceholder>
                    <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>Dashboard — Automated reporting & performance metrics</p>
                </div>
            </Full>

            <Midi style={{ marginBottom: 0 }}>
                <h3 id="financing" style={{ margin: "0 0 16px" }}>
                    3. Two-Click Financing
                </h3>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    To keep the wheels of commerce turning, dealers often need weekend financing. We simplified a complex eligibility and application process into a <mark>three-click journey:</mark>
                </p>
                <blockquote style={{ borderLeft: "3px solid var(--color-primary)", padding: "10px 20px", margin: "24px 0", fontStyle: "italic", color: "var(--color-primary)", fontFamily: "var(--font-display)", fontSize: "18px", letterSpacing: "-0.3px" }}>Request → Enter Amount → Submit.</blockquote>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    By integrating business rules and performance metrics into the UI, dealers could see their credit limit instantly — directly correlating to increased sales and shared revenue.
                </p>
            </Midi>
            <Full>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <ImagePlaceholder $height="300px">
                        <PlaceholderLabel>Image Placeholder</PlaceholderLabel>
                        <PlaceholderTitle>Financing flow — Request → Enter Amount → Submit</PlaceholderTitle>
                    </ImagePlaceholder>
                    <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>Financing flow — Request → Enter Amount → Submit (3 screens)</p>
                </div>
            </Full>

            <Midi style={{ marginBottom: 0 }}>
                <h3 id="role-management" style={{ margin: "0 0 16px" }}>
                    4. Granular Role Management
                </h3>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    I designed a robust <mark>role-based access control (RBAC) system.</mark> Whether you are a Dealer Principal, an Admin, or Internal Support, the UI adapts to your permissions — ensuring data protection and reducing the risk of accidental errors.
                </p>
            </Midi>
            <Full>
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                    <ImagePlaceholder $height="340px">
                        <PlaceholderLabel>Image Placeholder</PlaceholderLabel>
                        <PlaceholderTitle>Role management UI — Dealer Principal, Admin & Internal Support views</PlaceholderTitle>
                    </ImagePlaceholder>
                    <p style={{ fontSize: 13, opacity: 0.5, textAlign: "center", margin: "12px 0 0" }}>Role management UI — Dealer Principal, Admin & Internal Support views</p>
                </div>
            </Full>

            {/* ── TECHNICAL LEADERSHIP ── */}
            <Midi style={{ marginBottom: 0 }}>
                <h3 id="technical-leadership" style={{ margin: "0 0 24px" }}>
                    Technical Leadership & Execution
                </h3>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    As the lead, I knew that a pretty interface wouldn't survive a complex integration. I worked closely with engineering to ensure our <mark>Atomic Design system</mark> was feasible and future-proof.
                </p>
                <FlexGroup>
                    <div>
                        <h5>Layout & Roles</h5>
                        <p className="text-style">
                            Dual-role model for Dealers (Action) vs. Internal (Configure/Approve), built with role-based logic and persisted configurations.
                        </p>
                    </div>
                    <div>
                        <h5>State Handling</h5>
                        <p className="text-style">
                            Standardized UI for loading, empty, and error states across all modules with fallback logic and data deduplication.
                        </p>
                    </div>
                </FlexGroup>
                <p className="text-style">
                    I leveraged <mark>design tokens</mark> to ensure that as we added new modules — like the UBO compliance module — we could deploy them in days, not months. This module alone saved millions in potential KYC-related regulatory penalties.
                </p>
            </Midi>

            {/* ── IMPACT ── */}
            <Midi style={{ marginBottom: 0 }}>
                <h3 id="impact-and-results" style={{ margin: "0 0 16px" }}>
                    The Impact: By the Numbers
                </h3>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    The roll-out of the MVP wasn't just a launch; it was a transformation.
                </p>
                <ul style={{ listStyle: "none", margin: "20px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                        { label: "90M KES", body: "in orders transacted within the first month" },
                        { label: "10.6M KES", body: "in orders generated per hour at peak times" },
                        { label: "100% conversion", body: "on core journeys, validated via Microsoft Clarity" },
                        { label: "NPS 75.5", body: "a staggering achievement for a mandatory B2B enterprise tool" },
                    ].map(({ label, body }) => (
                        <li key={label} style={{ display: "flex", gap: 16, alignItems: "flex-start", background: "var(--color-surface)", borderRadius: 10, padding: "16px 20px" }}>
                            <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", background: "var(--color-primary)", marginTop: 7 }} />
                            <span className="text-style" style={{ margin: 0 }}>
                                <strong style={{ color: "var(--color-primary)", display: "block", marginBottom: 4 }}>{label}</strong>
                                {body}
                            </span>
                        </li>
                    ))}
                </ul>
            </Midi>

            {/* ── REFLECTIONS ── */}
            <Midi style={{ marginBottom: 0 }}>
                <h3 id="reflections" style={{ margin: "0 0 16px" }}>
                    Reflections: Designing for Scale
                </h3>
                <p className="text-style" style={{ marginBottom: 24 }}>
                    This project reinforced a core belief: <mark>B2B design isn't about flashy UI; it's about reducing friction and earning trust.</mark>
                </p>
                <ul style={{ listStyle: "none", margin: "20px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 12 }}>
                    {[
                        { label: "Seek Buy-in through POCs", body: 'A polished Proof of Concept early was the "unlock" that secured budget and dedicated developer resources.' },
                        { label: "Align on Metrics", body: "Anchoring every design decision to business KPIs made the value of UX indisputable to stakeholders." },
                        { label: "Collaborate at the Code Level", body: "Working closely with developers on state logic and data parsing ensured the final product matched the design vision." },
                    ].map(({ label, body }) => (
                        <li key={label} style={{ display: "flex", gap: 16, alignItems: "flex-start", background: "var(--color-surface)", borderRadius: 10, padding: "16px 20px" }}>
                            <span style={{ flexShrink: 0, width: 8, height: 8, borderRadius: "50%", background: "var(--color-primary)", marginTop: 7 }} />
                            <span className="text-style" style={{ margin: 0 }}>
                                <strong style={{ color: "var(--color-primary)", display: "block", marginBottom: 4 }}>{label}</strong>
                                {body}
                            </span>
                        </li>
                    ))}
                </ul>
            </Midi>
                </ContentColumn>
            </PageLayout>
            <SiteFooter />
        </Main>
    );
};

export { Scheduler };